import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const TARGET_EVENT_TYPES = ['hashchange', 'popstate', 'pageshow'] as const;

const getHref = () => document.location.href;

const addFallbackListeners = (updateHref: () => void) => {
  TARGET_EVENT_TYPES.forEach((eventType) => {
    window.addEventListener(eventType, updateHref);
  });

  const observer = new MutationObserver(updateHref);
  observer.observe(document, {
    childList: true,
    subtree: true
  });

  return observer;
};

const removeFallbackListeners = (
  updateHref: () => void,
  observer: MutationObserver | null
) => {
  TARGET_EVENT_TYPES.forEach((eventType) => {
    window.removeEventListener(eventType, updateHref);
  });

  if (observer) {
    observer.disconnect();
  }
};

export const useLocation = (): Location => {
  const [href, setHref] = useState(getHref);
  const animationFrameIdRef = useRef<number | null>(null);

  const updateHref = useCallback(() => {
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
    }

    animationFrameIdRef.current = requestAnimationFrame(() => {
      animationFrameIdRef.current = null;
      setHref(getHref);
    });
  }, []);

  useEffect(() => {
    // Navigation API: https://developer.mozilla.org/en-US/docs/Web/API/Navigation_API
    // @ts-expect-error Отсутствуют типы TS, т.к. Navigation API - новый стандарт
    const navApi = window.navigation;
    let observer: MutationObserver | null = null;

    if (navApi) {
      navApi.addEventListener('currententrychange', updateHref);
    } else {
      observer = addFallbackListeners(updateHref);
    }

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }

      if (navApi) {
        navApi.removeEventListener('currententrychange', updateHref);
      } else {
        removeFallbackListeners(updateHref, observer);
      }
    };
  }, [updateHref]);

  return useMemo(() => ({ ...document.location }), [href]);
};
